/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { AddToCartBehavior } from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
// @ts-ignore
import { ICartIconViewProps, ICartViewProps, IFlyoutCartLineItemViewProps } from '@mdsdyn365-commerce-modules/cart';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, getPayloadObject, INodeProps, KeyCodes, Modal, Node, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

import { ICartIconProps as ICartIconExtensionProps } from '../definition-extensions/cart-icon.ext.props.autogenerated';
import { readAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import { Address, AttributeTextValue, Cart } from '@msdyn365-commerce/retail-proxy';
import { CartLine, CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { CommercePropertyValueEnum } from '../../../modules/allowable-freight/utils/CommercePropertyValueEnum';
import { ICartState } from '@msdyn365-commerce/global-state';
import { removeWarehouseAttributes } from '../../../utils/cartAttributeManager';
/**
 * ICartIconViewState: Interface for Cart Icon View State.
 */
interface ICartIconViewState {
    isModalOpen: boolean;
    isAddressCheckComplete: boolean;
    isErrModalOpen: boolean;
    iProductsPresentVersion: number;
    shouldShowMiniCart: boolean;
}

// const renderInvoiceInCartMsg = (resources: ICartIconResources): JSX.Element | null => {
//     return <Node className={classnames('msc-cart-line__set-net-price')}>{resources.invoiceInCartMessage}</Node>;
// };

const _removeItemFromCartHandler = (props: ICartIconViewProps & ICartIconExtensionProps<{}>, cartlineToRemove: CartLine) => {
    const cart = props.data.cart.result ?? undefined;
    if (cart) {
        const input = {
            cartLineIds: [cartlineToRemove.LineId!.toString()]
        };
        cart.removeCartLines(input).catch((error: unknown) => {
            if (error instanceof Error) {
                props.telemetry.warning(error.message);
                props.telemetry.debug('Unable to Remove Cart Line');
            } else {
                props.telemetry.warning('An unknown error occurred');
                props.telemetry.debug('Unable to Remove Cart Line');
            }
        });
    }
};

const renderInvoiceCartLine = (
    cartLine: CartLine,
    props: ICartIconViewProps & ICartIconExtensionProps<{}>
): IFlyoutCartLineItemViewProps => {
    return (
        <div className='msc-cart-line'>
            <div className='msc-cart-line__content'>
                <div className='msc-cart-line__product'>
                    {MsDyn365.isBrowser ? <a className='msc-cart-line__product-title'>Invoice {cartLine.InvoiceId}</a> : null}
                    <div className='msc-cart-line__product__invoice-terms'></div>
                </div>

                <div className='msc-cart-line__product-savings'>
                    <span className={classnames('msc-price', props.className)}>
                        <span className='msc-cart-line__product-savings-actual'>
                            <span className='msc-price__actual' itemProp='price'>
                                {props.context.cultureFormatter.formatCurrency(cartLine.Price)}
                            </span>
                        </span>
                    </span>
                </div>
            </div>
            <Button
                className='msc-cart-line__remove-item'
                // onClick={props.removeItemClickHandler}
                onClick={e => _removeItemFromCartHandler(props, cartLine)}
                title={props.resources.removeCartButtonText}
                {...props.removeCartItemAttribute}
            >
                {props.resources.removeCartButtonText}
            </Button>

            {/* <div className='msc-price__actual'>{props.context.cultureFormatter.formatCurrency(cartLine.Price)}</div> */}
        </div>
    );
};

/**
 * Render Cart lines.
 * @param cartLines - Flyout cart line view props.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderCartlines = (
    cartLines: IFlyoutCartLineItemViewProps[] | undefined,
    props: ICartIconViewProps & ICartIconExtensionProps<{}>
): JSX.Element[] | null => {
    // if (props.data.cart.result?.hasInvoiceLine) {
    //     return [<Node {...props.miniCartItemWrapper}>{renderInvoiceInCartMsg(props.resources)}</Node>];
    // }
    const cart = props.data.cart.result ?? undefined;
    if (cart?.hasInvoiceLine) {
        const invoiceCartLines = cart.cart.CartLines;
        return invoiceCartLines.map((cartLine: CartLine, index: number) => {
            const cartLineIndex = index;
            return (
                <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                    <Node className={classnames('')}>{renderInvoiceCartLine(cartLine, props)}</Node>
                    {/* {cartLine.remove}
                    {cartLine.storeLocation} */}
                </Node>
            );
        });
    }

    if (!cartLines) {
        props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }

    if (MsDyn365.isBrowser) {
        document.addEventListener('UPDATE_CART', (data: any) => {
            if (data.detail.Products?.length) {
                const cartState = props.data.cart.result;
                cartState?.removeCartLines({ cartLineIds: data.detail.Products.map((p: any) => p.LineId) });
            }
        });
    }

    return cartLines.map((cartLine, index) => {
        const cartLineIndex = index;
        return (
            <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                <Node className={classnames(cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '')}>{cartLine.cartline}</Node>
                {cartLine.remove}
                {cartLine.storeLocation}
            </Node>
        );
    });
};

/**
 * Initiate total price.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderTotalPrice = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    const price = cart && !cart.hasInvoiceLine && (cart.cart.TotalAmount || undefined);
    const subtotal = !price && cart && cart.cart && cart.cart.SubtotalAmount ? cart.cart.SubtotalAmount : undefined;
    const totalPrice = price
        ? props.context.cultureFormatter.formatCurrency(price)
        : subtotal
        ? props.context.cultureFormatter.formatCurrency(subtotal)
        : props.context.cultureFormatter.formatCurrency(props.resources.emptyPrice);
    return (
        <div className='ms-cart-icon__subTotalText'>
            <span className='ms-cart-icon__subTotalPriceText'>
                {props.resources.totalPriceFormatString?.replace('{price}', '')}
                <span className='ms-cart-icon__subTotalPrice'>{totalPrice}</span>
            </span>
        </div>
    );
};

/**
 *
 * CartIconView component.
 * @extends {React.PureComponent<ICartIconViewProps>}
 */
export class CartIconView extends React.PureComponent<
    ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<{}>,
    ICartIconViewState
> {
    private isAutoDisplayTriggered: boolean;
    private isErrAutoDisplayTriggered: boolean;
    private ALLOWABLE_FREIGHT_ATTRIBUTE_KEY = 'AllowableFreightAmount';
    private INVOICE_AMOUNT_ATTRIBUTE_KEY = 'InvoiceAmountDue';

    public constructor(props: ICartViewProps & ICartIconViewState & ICartIconViewProps & ICartIconExtensionProps<{}>) {
        super(props);
        this.isAutoDisplayTriggered = false;
        this.isErrAutoDisplayTriggered = false;
        this.state = {
            isModalOpen: false,
            isAddressCheckComplete: false,
            isErrModalOpen: false,
            iProductsPresentVersion: 0,
            shouldShowMiniCart: false
        };
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            window.addEventListener('keydown', this._escFunction, false);
            document.addEventListener('INVOICE_ADDED', this.handleInvoiceAdded, false);
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('keydown', this._escFunction, false);
            document.removeEventListener('INVOICE_ADDED', this.handleInvoiceAdded, false);
        }
    }

    private getAllowableFreightAttIdx = (): number => {
        return (
            this.props.data.cart.result?.cart.AttributeValues?.findIndex(
                (attribute: AttributeTextValue) => attribute.Name === this.ALLOWABLE_FREIGHT_ATTRIBUTE_KEY
            ) ?? -1
        );
    };

    private getInvoiceAmountDueAttIdx = (): number => {
        return (
            this.props.data.cart.result?.cart.AttributeValues?.findIndex(
                (attribute: AttributeTextValue) => attribute.Name === this.INVOICE_AMOUNT_ATTRIBUTE_KEY
            ) ?? -1
        );
    };

    //    private getCouponCodeAttIdx = (): number => {
    //        return (
    //            this.props.data.cart.result?.cart.AttributeValues?.findIndex(
    //                (attribute: AttributeTextValue) => attribute.Name === 'coupon-code'
    //            ) ?? -1
    //        );
    //    };

    private _removeCommentFromCartLine(): void {
        const cartState: ICartState = this.props.data.cart.result || undefined;

        if (cartState) {
            const _clonedCart: Cart = { ...cartState.cart };
            let isUpdatedNeeded: boolean = false;

            if ((!cartState.cart.CartLines || cartState.cart.CartLines.length === 0) && cartState.cart.Comment) {
                _clonedCart.Comment = '';
                isUpdatedNeeded = true;
            }

            const cartLines: CartLine[] | undefined = cartState.cart.CartLines;
            const hasInvoiceLines = cartLines?.some(line => line.IsInvoiceLine);

            //    const couponCodeIdx = this.getCouponCodeAttIdx();
            //    if (couponCodeIdx !== -1 && hasInvoiceLines) {
            //        const attributeArr: AttributeTextValue[] = _clonedCart.AttributeValues || [];
            //        attributeArr[couponCodeIdx] = {
            //            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
            //            Name: 'coupon-code',
            //            ExtensionProperties: [],
            //            TextValue: ' ',
            //            TextValueTranslations: []
            //        } as AttributeTextValue;

            //        _clonedCart.AttributeValues = attributeArr;
            //        isUpdatedNeeded = true;
            //    }

            const allowableFreightIdx = this.getAllowableFreightAttIdx();
            if (allowableFreightIdx !== -1) {
                const attributeArr: AttributeTextValue[] = _clonedCart.AttributeValues || [];
                let freightData: { invoiceId: string; amount: number }[] = JSON.parse(attributeArr[allowableFreightIdx].TextValue || '[]');

                if (!hasInvoiceLines) {
                    attributeArr.splice(allowableFreightIdx, 1);
                    isUpdatedNeeded = true;
                } else {
                    const updatedFreightData = freightData.filter(item => cartLines?.some(line => line.InvoiceId === item.invoiceId));

                    if (updatedFreightData.length !== freightData.length) {
                        attributeArr[allowableFreightIdx] = {
                            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                            Name: this.ALLOWABLE_FREIGHT_ATTRIBUTE_KEY,
                            ExtensionProperties: [],
                            TextValue: JSON.stringify(updatedFreightData),
                            TextValueTranslations: []
                        } as AttributeTextValue;

                        _clonedCart.AttributeValues = attributeArr;
                        isUpdatedNeeded = true;
                    }
                }
            }

            const invoiceAmountDueIdx = this.getInvoiceAmountDueAttIdx();
            if (invoiceAmountDueIdx !== -1) {
                const attributeArr: AttributeTextValue[] = _clonedCart.AttributeValues || [];
                let invoiceData: { invoiceId: string; amountDue: number }[] = JSON.parse(
                    attributeArr[invoiceAmountDueIdx].TextValue || '[]'
                );

                if (!hasInvoiceLines) {
                    attributeArr.splice(invoiceAmountDueIdx, 1);
                    isUpdatedNeeded = true;
                } else {
                    const updatedInvoiceData = invoiceData.filter(item => cartLines?.some(line => line.InvoiceId === item.invoiceId));

                    if (updatedInvoiceData.length !== invoiceData.length) {
                        attributeArr[invoiceAmountDueIdx] = {
                            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                            Name: this.INVOICE_AMOUNT_ATTRIBUTE_KEY,
                            ExtensionProperties: [],
                            TextValue: JSON.stringify(updatedInvoiceData),
                            TextValueTranslations: []
                        } as AttributeTextValue;

                        _clonedCart.AttributeValues = attributeArr;
                        isUpdatedNeeded = true;
                    }
                }
            }

            if (isUpdatedNeeded) {
                void cartState.updateCart({ newCartObject: _clonedCart });
            }
        }
    }

    private async getAddresses(): Promise<Address | undefined> {
        if (this.props.context.request.user.isAuthenticated) {
            const customer = await readAsync(
                { callerContext: this.props.context.actionContext },
                this.props.context.request.user.customerAccountNumber!
            );
            if (customer) {
                return customer.Addresses?.find(add => add.IsPrimary);
            }
        }
        return undefined;
    }

    private async handleCartAddress(): Promise<void> {
        const cartState = this.props.data.cart.result;
        if (cartState) {
            this.setState({ isAddressCheckComplete: true });
            const hasAddress = !!cartState.cart.ShippingAddress;
            // console.log(hasAddress, 'has address')
            if (!hasAddress) {
                const primaryAddress = await this.getAddresses();
                // console.log(primaryAddress, 'primary address')

                if (!!primaryAddress) {
                    void (await cartState.updateShippingAddress({ newShippingAddress: primaryAddress }));
                }
            }
        }
    }

    private getExtensionPropertyValueByKey = (
        properties: CommerceProperty[] | undefined,
        key: string,
        valueType: CommercePropertyValueEnum
    ) => {
        const property = properties?.find(p => p.Key === key)?.Value;

        // eslint-disable-next-line security/detect-object-injection
        const val = property ? property[valueType] : null;
        return val;
    };

    // private _removeWarehouseAttribute = async () => {
    //     const cartState: ICartState = this.props.data.cart.result || undefined;
    //     const authorizedWarehouseAccounts: string[] = this.props.context.app.config.authorizedWarehouseAccounts || [];
    //     const isAlternateWarehouseFeatureEnabled: boolean = this.props.context.app.config.enableAlternateWarehouseFeature;
    //     const customerAccNum = this.props.context.request.user.customerAccountNumber;
    //     const hasAccess = isAlternateWarehouseFeatureEnabled || (customerAccNum && authorizedWarehouseAccounts.includes(customerAccNum));

    //     if (hasAccess || !cartState) {
    //         return;
    //     }

    //     let changesMade = false;
    //     const updatedCartLines = cartState.cart.CartLines?.map(cartline => {
    //         let needsUpdate = false;
    //         const updatedAttributes = cartline.AttributeValues?.map((attr: AttributeTextValue) => {
    //             if (attr.Name === 'IsWarehouseOverride' && attr.TextValue !== 'false') {
    //                 needsUpdate = true;
    //                 return { ...attr, TextValue: 'false' };
    //             } else if (attr.Name === 'WarehouseId' && attr.TextValue !== '') {
    //                 needsUpdate = true;
    //                 return { ...attr, TextValue: '' };
    //             }
    //             return attr;
    //         });

    //         if (needsUpdate) {
    //             changesMade = true;
    //             return { ...cartline, AttributeValues: updatedAttributes };
    //         }
    //         return cartline;
    //     });

    //     if (!changesMade || !updatedCartLines) {
    //         return;
    //     }

    //     try {
    //         await cartState.updateCartLines({ updatedCartLinesObject: updatedCartLines ?? [] });
    //     } catch (e) {
    //         console.error('Error removing warehouse attributes', e);
    //     }
    // };

    /**
     * Render Cart Item count.
     * @returns JSX Element.
     */
    public render(): JSX.Element | null {
        this._removeCommentFromCartLine();
        // this._removeWarehouseAttribute();
        const cart: ICartState = this.props.data.cart.result ?? undefined;
        const authorizedWarehouseAccounts = this.props.context.app.config.authorizedWarehouseAccounts || [];
        const isAlternateWarehouseFeatureEnabled = this.props.context.app.config.enableAlternateWarehouseFeature ?? false;
        removeWarehouseAttributes(
            cart,
            authorizedWarehouseAccounts,
            isAlternateWarehouseFeatureEnabled,
            this.props.context.request.user.customerAccountNumber
        );

        let iProductsPresent = this.getExtensionPropertyValueByKey(
            cart?.cart?.ExtensionProperties,
            'not-eligible',
            CommercePropertyValueEnum.string
        );
        // console.log(iProductsPresent)
        // console.log(this.state.iProductsPresentVersion, !!this.state.iProductsPresentVersion)
        if (!this.isErrAutoDisplayTriggered && typeof iProductsPresent === 'string' && iProductsPresent !== '[]') {
            this.setState({ isErrModalOpen: true, iProductsPresentVersion: cart?.cart.Version || 0 });
            this.isErrAutoDisplayTriggered = true;
        } else if (!this.state.isErrModalOpen && cart?.cart.Version !== this.state.iProductsPresentVersion) {
            this.isErrAutoDisplayTriggered = false;
        }

        if (this.state.isErrModalOpen) {
            this.isAutoDisplayTriggered = true;
        }

        let ippContent;

        if (!!iProductsPresent) {
            try {
                ippContent = (
                    <>
                        <p>{this.props.resources.ineligibleProductsText}</p>
                        {(JSON.parse(iProductsPresent as string) as CartLine[]).map(r => (
                            <li>
                                {r.Barcode}: {r.Description}
                            </li>
                        ))}
                    </>
                );
            } catch (err) {}
        }

        if (cart && !this.state.isAddressCheckComplete) {
            this.handleCartAddress();
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- check config.
        const shouldShowMiniCart =
            (this.state.shouldShowMiniCart || cart?.isProductAddedToCart) &&
            this.props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;
        this.props.removeItemClickHandler;

        if (shouldShowMiniCart) {
            if (!this.isAutoDisplayTriggered) {
                // First time trigger auto mini cart.
                this.setState({ isModalOpen: true });
                this.isAutoDisplayTriggered = true;
            } else if (!this.state.isModalOpen) {
                // If modal is closed, reset the flag.
                this.isAutoDisplayTriggered = false;
            }
        }

        // Resolving ticket 8253: Remove "Checkout" option; only allow "Go to Shopping Cart".

        const itemCount = this.props.data.cart.result?.cart.CartLines.length || 0;
        const goToCartButtonText = this.props.resources.goToCartButtonTitleNew.replace('{count}', itemCount.toString());

        return (
            <Node {...this.props.miniCartWrapper} {...this.props.renderModuleAttributes(this.props)}>
                <button onClick={this._openModal} className='ms-cart-icon-wrapper msc-btn'>
                    {this.props.cartIcon}
                </button>
                <Node {...this._modalErrContainer()}>{ippContent && ippContent}</Node>
                <Node {...this._modalContainer()}>
                    <Node className='ms-cart-icon__header msc-modal__header'>
                        {this.props.flyoutTitle}
                        {this._renderCartItemCount(this.props)}
                        {this._renderCartCloseIcon()}
                    </Node>
                    <Node className='ms-cart-icon__body msc-modal__body'>
                        {this.props.slots.allowableFreight}
                        {this.props.data.cart.result?.isEmpty ? this.props.slots.emptyCart : null}
                        <Node {...this.props.CartlinesWrapper}>{renderCartlines(this.props.cartlines, this.props)}</Node>
                    </Node>
                    <Node className='ms-cart-icon__footer msc-modal__footer'>
                        {this.props.slots.promoContentItem}
                        {renderTotalPrice(this.props)}
                        <p className='ms-cart-icon__subtotal-message'>{this.props.resources.subTotalMessage}</p>
                        {/* {this.props.checkoutAsSignInUserButton}
                        {this.props.checkoutAsGuestButton} */}

                        {this.props.data.cart.result?.isEmpty ? null : (
                            <Button
                                className='ms-cart-icon__btn-gotocart'
                                href={this.props.goToCartButton.props.href}
                                title={goToCartButtonText}
                                {...this.props.goToCartButton.props}
                            >
                                {goToCartButtonText}
                            </Button>
                        )}
                        <Node className='ms-cart-icon__btn-section'>
                            {this.props.data.cart.result?.isEmpty ? (
                                <Button
                                    className='ms-cart-icon__btn-backtoshopping'
                                    href={getUrlSync('home', this.props.context.actionContext)}
                                >
                                    {this.props.resources.continueShoppingButtonTitle}
                                </Button>
                            ) : null}
                        </Node>
                    </Node>
                </Node>
            </Node>
        );
    }

    /**
     * Initiate modal container.
     * @returns Inode props.
     */
    private readonly _modalContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-cart-icon__minicartmodal-container',
            wrapClassName: 'ms-cart-icon__minicartmodal',
            isOpen: this.state.isModalOpen,
            toggle: this.closeModal
        };
    };

    /**
     * Initiate open modal.
     */
    private readonly _openModal = (): void => {
        const payLoad = getPayloadObject('click', this.props.telemetryContent!, 'cart-icon', '');
        onTelemetryClick(this.props.telemetryContent!, payLoad, 'cart-icon');
        this.setState({
            isModalOpen: true
        });
    };

    private readonly _modalErrContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-cart-icon__minicarterrmodal-container',
            wrapClassName: 'ms-cart-icon__minicarterrmodal',
            isOpen: this.state.isErrModalOpen,
            toggle: this.closeErrModal
        };
    };

    private readonly closeErrModal = (): void => {
        this.setState({
            isErrModalOpen: false
        });

        this.isAutoDisplayTriggered = true;
    };

    /**
     * Initiate close modal.
     */
    private readonly closeModal = (): void => {
        this.setState({
            isModalOpen: false,
            shouldShowMiniCart: false
        });
    };

    /**
     * Initiate cart close Icon.
     * @returns JSX Element.
     */
    private readonly _renderCartCloseIcon = (): JSX.Element | null => {
        return <Button className='ms-cart-icon__btn-close' aria-label='Close' onClick={this.closeModal} />;
    };

    /**
     * Render Cart Item count.
     * @param props - Cart icon view props.
     * @returns JSX Element.
     */
    private readonly _renderCartItemCount = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
        let cartItemCount;
        const defaultCartItemCount: number = 0;
        const defaultCartItemCountOne: number = 1;

        const cart = props.data.cart.result ?? undefined;

        if (ArrayExtensions.hasElements(props.cartlines)) {
            if (props.cartlines.length === defaultCartItemCountOne) {
                cartItemCount = `${defaultCartItemCountOne} ${props.resources.item}`;
            } else {
                cartItemCount = `${props.cartlines.length} ${props.resources.items}`;
            }
        } else if (cart?.hasInvoiceLine && cart.cart.CartLines) {
            cartItemCount =
                cart.cart.CartLines.length == 1
                    ? `${cart.cart.CartLines.length} ${props.resources.item}`
                    : `${cart.cart.CartLines.length} ${props.resources.items}`;
        } else {
            cartItemCount = `${defaultCartItemCount} ${props.resources.items}`;
        }
        return (
            <Node className='ms-cart-icon__count' tabIndex='0'>
                {cartItemCount}
            </Node>
        );
    };

    /**
     * Handle escape click to close modal.
     * @param event - On press of any key.
     */
    private readonly _escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === KeyCodes.Escape && this.state.isModalOpen) {
            this.closeModal();
        }
    };

    private handleInvoiceAdded = (data: any) => {
        this.setState({ shouldShowMiniCart: true });
    };
}

export default CartIconView;
